import { useState, useEffect, useRef } from 'react';
import { AvailableDate } from '../types/calenderTypes';

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Calendar Hook - Manual Availability Implementation
 *
 * This hook provides manual availability management for the calendar.
 * Users can mark dates as available or busy by clicking on them.
 */
export const useCalendar = () => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  // Calendar data state
  const [dateRangeStatus, setDateRangeStatus] = useState<AvailableDate[]>([]);

  // UI state for filtering and display
  const [availableFilter, setAvailableFilter] = useState<'all' | 'weekdays' | 'weekends'>('all');
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [activeMonth, setActiveMonth] = useState(new Date());
  const [recentlyClickedDate, setRecentlyClickedDate] = useState<Date | null>(null);
  const [statusViewFilter, setStatusViewFilter] = useState<'available' | 'busy' | 'both'>('both');

  // Memory for manually toggled dates
  const statusMemoryRef = useRef<Record<string, boolean>>({});

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const handleDayOfWeekToggle = (dayIndex: number) => {
    const newSelectedDays = selectedDaysOfWeek.includes(dayIndex)
      ? selectedDaysOfWeek.filter(d => d !== dayIndex)
      : [...selectedDaysOfWeek, dayIndex];

    const sortedNewSelectedDays = newSelectedDays.sort();
    setSelectedDaysOfWeek(sortedNewSelectedDays);

    // Auto-update filter based on selection
    const isAll = sortedNewSelectedDays.length === 7;
    const isWeekdays =
      sortedNewSelectedDays.length === 5 && sortedNewSelectedDays.every(d => d >= 1 && d <= 5);
    const isWeekends =
      sortedNewSelectedDays.length === 2 &&
      sortedNewSelectedDays.includes(0) &&
      sortedNewSelectedDays.includes(6);

    if (isAll) {
      setAvailableFilter('all');
    } else if (isWeekdays) {
      setAvailableFilter('weekdays');
    } else if (isWeekends) {
      setAvailableFilter('weekends');
    } else {
      setAvailableFilter('all');
    }
  };

  const handleAvailableFilterChange = (filter: 'all' | 'weekdays' | 'weekends') => {
    setAvailableFilter(filter);
    if (filter === 'all') {
      setSelectedDaysOfWeek([0, 1, 2, 3, 4, 5, 6]);
    } else if (filter === 'weekdays') {
      setSelectedDaysOfWeek([1, 2, 3, 4, 5]);
    } else if (filter === 'weekends') {
      setSelectedDaysOfWeek([0, 6]);
    }
  };

  const handleDayClick = (date: Date) => {
    const dateString = date.toDateString();
    const day = date.getDay();

    // Check if this date is currently in the filter
    const isInCurrentFilter = (() => {
      if (availableFilter === 'weekdays') {
        return day >= 1 && day <= 5;
      }
      if (availableFilter === 'weekends') {
        return day === 0 || day === 6;
      }
      return selectedDaysOfWeek.includes(day);
    })();

    setDateRangeStatus(currentStatus => {
      const existingDate = currentStatus.find(d => d.date.toDateString() === dateString);

      if (existingDate) {
        if (existingDate.isManuallyAdded) {
          // Record last status before opt-out
          statusMemoryRef.current[dateString] = existingDate.isAvailable;
          // Remove opt-in date on second click
          return currentStatus.filter(d => d.date.toDateString() !== dateString);
        } else if (!isInCurrentFilter) {
          // If not in filter, convert to opt-in
          return currentStatus.map(d =>
            d.date.toDateString() === dateString
              ? { ...d, isAvailable: true, isManuallyAdded: true }
              : d
          );
        } else {
          // Toggle availability for filtered date
          return currentStatus.map(d =>
            d.date.toDateString() === dateString ? { ...d, isAvailable: !d.isAvailable } : d
          );
        }
      } else {
        // Add as opt-in if not in filter
        const lastStatus = statusMemoryRef.current[dateString];
        const newDate: AvailableDate = {
          date: new Date(date),
          isAvailable: lastStatus !== undefined ? lastStatus : true,
          dayOfWeek: dayNames[date.getDay()],
          events: [],
          isManuallyAdded: !isInCurrentFilter,
        };
        return [...currentStatus, newDate].sort((a, b) => a.date.getTime() - b.date.getTime());
      }
    });

    setRecentlyClickedDate(date);
    setTimeout(() => setRecentlyClickedDate(null), 2000);
  };

  // Filter reconciliation: restore previously opted-out dates with last known status
  useEffect(() => {
    // Get all dates in the import window
    const allDatesInWindow = (() => {
      const dateMap: Record<string, AvailableDate> = {};
      dateRangeStatus.forEach(d => {
        dateMap[d.date.toDateString()] = d;
      });
      // Add any dates from statusMemoryRef not in dateRangeStatus
      Object.keys(statusMemoryRef.current).forEach(dateString => {
        if (!dateMap[dateString]) {
          dateMap[dateString] = {
            date: new Date(dateString),
            dayOfWeek: dayNames[new Date(dateString).getDay()],
            isAvailable: statusMemoryRef.current[dateString],
            events: [],
            isManuallyAdded: false,
          };
        }
      });
      return Object.values(dateMap);
    })();

    // Determine which dates should be in the filter
    const shouldBeFiltered = (date: Date) => {
      const day = date.getDay();
      if (availableFilter === 'weekdays') return day >= 1 && day <= 5;
      if (availableFilter === 'weekends') return day === 0 || day === 6;
      return selectedDaysOfWeek.includes(day);
    };

    setDateRangeStatus(currentStatus => {
      const dateStrings = currentStatus.map(d => d.date.toDateString());
      const toAdd = allDatesInWindow
        .filter(d => shouldBeFiltered(d.date) && !dateStrings.includes(d.date.toDateString()))
        .map(d => ({
          ...d,
          isManuallyAdded: false, // Now part of the filter
        }));
      if (toAdd.length === 0) return currentStatus;
      return [...currentStatus, ...toAdd].sort((a, b) => a.date.getTime() - b.date.getTime());
    });
  }, [availableFilter, selectedDaysOfWeek, dateRangeStatus]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // First filter by day-of-week
  const dayOfWeekFilteredDates = dateRangeStatus.filter(dateInfo => {
    // Safety check: ensure dateInfo.date is a valid Date object
    if (!dateInfo.date || typeof dateInfo.date.getDay !== 'function') {
      console.warn('Invalid date object in dateRangeStatus:', dateInfo);
      return false; // Skip invalid dates
    }

    const day = dateInfo.date.getDay();
    if (availableFilter === 'weekdays') return day >= 1 && day <= 5;
    if (availableFilter === 'weekends') return day === 0 || day === 6;
    return selectedDaysOfWeek.includes(day);
  });

  // Then filter by status
  const statusFilteredDates = dayOfWeekFilteredDates.filter(date => {
    if (statusViewFilter === 'available') return date.isAvailable;
    if (statusViewFilter === 'busy') return !date.isAvailable;
    return true;
  });

  // ============================================================================
  // RETURN INTERFACE
  // ============================================================================

  return {
    // Data state
    dateRangeStatus,

    // UI state
    activeMonth,
    availableFilter,
    selectedDaysOfWeek,
    statusViewFilter,
    recentlyClickedDate,

    // Computed values
    filteredDates: dayOfWeekFilteredDates,
    statusFilteredDates,

    // State setters
    setAvailableFilter,
    setSelectedDaysOfWeek,
    setActiveMonth,
    setStatusViewFilter,
    setRecentlyClickedDate,

    // UI interactions
    handleDayClick,
    handleDayOfWeekToggle,
    handleAvailableFilterChange,
  };
};
